import * as THREE from 'three';
import { GLTFLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import EventEmitter from './EventEmitter';
import Application from '../Application';

interface LoadedItems {
    gltfModel: { [key: string]: GLTF };
    texture: { [key: string]: THREE.Texture };
    audio: { [key: string]: HTMLAudioElement };
    cubeTexture: { [key: string]: THREE.CubeTexture };
}

export default class Resources extends EventEmitter {
    sources: Resource[];
    items: LoadedItems;
    toLoad: number;
    loaded: number;
    private gltfLoader: GLTFLoader;
    private textureLoader: THREE.TextureLoader;
    private cubeLoader: THREE.CubeTextureLoader;
    private settled = new Set<string>();
    private timeouts = new Map<string, number>();
    private readonly loadTimeoutMs = 12000;

    constructor(sources: Resource[]) {
        super();
        this.sources = sources;
        this.items = { gltfModel: {}, texture: {}, audio: {}, cubeTexture: {} };
        this.toLoad = sources.length;
        this.loaded = 0;

        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('draco/gltf/');
        this.gltfLoader = new GLTFLoader();
        this.gltfLoader.setDRACOLoader(dracoLoader);
        this.textureLoader = new THREE.TextureLoader();
        this.cubeLoader = new THREE.CubeTextureLoader();

        this.startLoading();
    }

    private startLoading() {
        if (this.sources.length === 0) {
            this.trigger('ready');
            return;
        }
        for (const src of this.sources) {
            this.startTimeout(src);
            switch (src.type) {
                case 'gltfModel':
                    this.gltfLoader.load(
                        src.path,
                        (gltf) => this.complete(src.name, 'gltfModel', gltf),
                        undefined,
                        (err) => this.fail(src.path, err)
                    );
                    break;
                case 'texture':
                    this.textureLoader.load(
                        src.path,
                        (tex) => this.complete(src.name, 'texture', tex),
                        undefined,
                        (err) => this.fail(src.path, err)
                    );
                    break;
                case 'cubeTexture':
                    this.cubeLoader.load(
                        src.path,
                        (tex) => this.complete(src.name, 'cubeTexture', tex)
                    );
                    break;
                case 'audio': {
                    const audio = new Audio();
                    audio.preload = 'auto';
                    audio.addEventListener('canplaythrough', () =>
                        this.complete(src.name, 'audio', audio)
                    );
                    audio.addEventListener('error', (e) => this.fail(src.path, e));
                    audio.src = src.path;
                    break;
                }
            }
        }
    }

    private startTimeout(src: Resource) {
        const timer = window.setTimeout(() => {
            this.fail(src.path, new Error(`Timed out after ${this.loadTimeoutMs}ms`));
        }, this.loadTimeoutMs);
        this.timeouts.set(src.name, timer);
    }

    private complete(name: string, type: keyof LoadedItems, item: unknown) {
        if (this.settled.has(name)) return;
        this.settled.add(name);
        const timer = this.timeouts.get(name);
        if (timer) {
            window.clearTimeout(timer);
            this.timeouts.delete(name);
        }
        // @ts-ignore
        this.items[type][name] = item;
        this.loaded += 1;
        const app = new Application();
        app.loading?.setProgress(this.loaded, this.toLoad);
        if (this.loaded === this.toLoad) {
            this.trigger('ready');
        }
    }

    private fail(path: string, err: unknown) {
        const source = this.sources.find((src) => src.path === path);
        const name = source?.name ?? path;
        if (this.settled.has(name)) return;
        this.settled.add(name);
        const timer = this.timeouts.get(name);
        if (timer) {
            window.clearTimeout(timer);
            this.timeouts.delete(name);
        }
        console.warn(`[Resources] failed to load ${path}`, err);
        this.loaded += 1;
        const app = new Application();
        app.loading?.setProgress(this.loaded, this.toLoad);
        if (this.loaded === this.toLoad) {
            this.trigger('ready');
        }
    }
}
